// Lib
import { useDispatch } from 'react-redux';
import { List } from 'immutable';

// Utils
import { getTags } from '../../../../../../../common/elements/utils/elementPropertyUtils';

// Actions
import { removeTagFromElements } from '../../../../../../element/tags/elementTagActions';
import { addTagToElementsThunk, createAndAddTagToElementsThunk } from '../tagThunks';

// Types
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { Tag } from '../../../../../../../common/tags/tagTypes';
import { TagsToolContentProps } from '../tagComponentPropTypes';
import { TagsByBoards } from '../tagUtils';

type ReturnType = {
    handleToggleTagSelection: (tag: Tag) => void;
    handleCreateTag: (rootBoardId?: string) => void;
    handleCreateCopyAndAddToElement: (tag: Tag) => void;
    handleSearchSubmit: () => void;
};

const useTagHandlers = (
    props: TagsToolContentProps,
    searchString: string,
    setSearchString: (searchString: string) => void,
    elements: List<ImMNElement>,
    tagsByBoards: TagsByBoards,
): ReturnType => {
    const { currentBoardId, selectedElements, currentUserId, closePopups, shareRootPath } = props;

    const dispatch = useDispatch();

    const hasTagOnAllElements = (tagId: string) => elements.every((element) => getTags(element)?.includes(tagId));

    const handleAddTagToElements = (tag: Tag) => {
        dispatch(addTagToElementsThunk(tag, elements, currentBoardId, shareRootPath));
    };

    const handleToggleTagSelection = (tag: Tag) => {
        if (hasTagOnAllElements(tag.id)) {
            dispatch(removeTagFromElements(elements, tag.id));
        } else {
            handleAddTagToElements(tag);
        }

        // If the user is filtering the tags close the popup when they select a tag
        if (searchString) closePopups();
    };

    const handleCreateTag = (rootBoardId = currentBoardId) => {
        if (!searchString) return;

        const tagData = {
            name: searchString,
            rootBoardIds: [rootBoardId],
            creator: currentUserId,
        };

        dispatch(createAndAddTagToElementsThunk(tagData, selectedElements, currentBoardId, shareRootPath));

        setSearchString('');
        closePopups();
    };

    const handleCreateCopyAndAddToElement = (tag: Tag) => {
        const { name, color } = tag;
        const tagData = {
            name,
            color,
            rootBoardIds: [currentBoardId],
            creator: currentUserId,
        };

        dispatch(createAndAddTagToElementsThunk(tagData, selectedElements, currentBoardId, shareRootPath));
    };

    const handleSearchSubmit = () => {
        const allTags = tagsByBoards.flatMap((board) => board.tags);
        const existingTag = allTags.find((tag) => tag.name.toLowerCase() === searchString.toLowerCase());

        if (!existingTag) {
            handleCreateTag();
            return;
        }

        if (!hasTagOnAllElements(existingTag.id)) handleAddTagToElements(existingTag);

        closePopups();
    };

    return {
        handleToggleTagSelection,
        handleCreateTag,
        handleCreateCopyAndAddToElement,
        handleSearchSubmit,
    };
};

export default useTagHandlers;
